import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { helpAPI } from '../../services/api';

const ROLE_OPTIONS = [
  'all',
  'teacher',
  'teacher_supervisor',
  'teacher_admin',
  'sales',
  'sales_supervisor',
  'country_manager',
  'finance',
  'admin',
];

const emptyForm = () => ({
  id: '',
  title: '',
  shortAnswer: '',
  bodyMarkdown: '',
  status: 'draft',
  roles: ['all'],
  topics: [],
  aliases: '',
  relatedIds: '',
  sortOrder: 100,
  lastVerified: new Date().toISOString().slice(0, 10),
});

function csvToList(s) {
  return String(s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function HelpAdminPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [gapStatus, setGapStatus] = useState('new');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [uploadArticleId, setUploadArticleId] = useState('');
  const [gapNotes, setGapNotes] = useState({});

  const topicsQuery = useQuery(['help-topics'], async () => {
    const res = await helpAPI.getTopics();
    return res.data.data || [];
  });

  const articlesQuery = useQuery(['help-admin-articles', statusFilter], async () => {
    const res = await helpAPI.adminListArticles(statusFilter ? { status: statusFilter } : {});
    return res.data.data || [];
  });

  const gapsQuery = useQuery(['help-admin-gaps', gapStatus], async () => {
    const res = await helpAPI.adminListGaps(gapStatus ? { status: gapStatus } : {});
    return res.data.data || [];
  });

  const saveMutation = useMutation(
    async () => {
      const payload = {
        id: form.id.trim(),
        title: form.title.trim(),
        shortAnswer: form.shortAnswer.trim(),
        bodyMarkdown: form.bodyMarkdown,
        status: form.status,
        roles: form.roles.length ? form.roles : ['all'],
        topics: form.topics,
        aliases: csvToList(form.aliases),
        relatedIds: csvToList(form.relatedIds),
        lastVerified: form.lastVerified || null,
        sortOrder: Number(form.sortOrder) || 100,
      };
      if (editingId) return helpAPI.adminUpdateArticle(editingId, payload);
      return helpAPI.adminCreateArticle(payload);
    },
    {
      onSuccess: () => {
        toast.success('Article saved');
        setDialogOpen(false);
        setEditingId(null);
        setForm(emptyForm());
        queryClient.invalidateQueries(['help-admin-articles']);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
    }
  );

  const statusMutation = useMutation(
    ({ id, status }) => helpAPI.adminSetStatus(id, { status }),
    {
      onSuccess: () => {
        toast.success('Status updated');
        queryClient.invalidateQueries(['help-admin-articles']);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Update failed'),
    }
  );

  const deleteMutation = useMutation((id) => helpAPI.adminDeleteArticle(id), {
    onSuccess: () => {
      toast.success('Article deleted');
      queryClient.invalidateQueries(['help-admin-articles']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const seedMutation = useMutation(() => helpAPI.adminSeedFromDisk(), {
    onSuccess: (res) => {
      toast.success(`Seeded ${res.data?.data?.count || 0} articles from disk`);
      queryClient.invalidateQueries(['help-admin-articles']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Seed failed'),
  });

  const gapMutation = useMutation(
    ({ id, ...body }) => helpAPI.adminUpdateGap(id, body),
    {
      onSuccess: () => {
        toast.success('Gap updated');
        queryClient.invalidateQueries(['help-admin-gaps']);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Update failed'),
    }
  );

  const topicOptions = topicsQuery.data || [];
  const articleOptions = useMemo(
    () => (articlesQuery.data || []).map((a) => ({ id: a.id, title: a.title })),
    [articlesQuery.data]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await helpAPI.adminGetArticle(id);
      const a = res.data.data;
      setEditingId(id);
      setForm({
        id: a.id,
        title: a.title,
        shortAnswer: a.shortAnswer,
        bodyMarkdown: a.bodyMarkdown || '',
        status: a.status,
        roles: a.roles?.length ? a.roles : ['all'],
        topics: a.topics || [],
        aliases: (a.aliases || []).join(', '),
        relatedIds: (a.relatedIds || []).join(', '),
        sortOrder: a.sortOrder || 100,
        lastVerified: a.lastVerified ? String(a.lastVerified).slice(0, 10) : '',
      });
      setUploadArticleId(id);
      setDialogOpen(true);
    } catch (e) {
      toast.error('Failed to load article');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Help Admin
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Manage published answers, file attachments, and unanswered questions. Short answer is
        required for every article; uploads are optional supporting docs.
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Articles" />
        <Tab label="Unanswered / Gaps" />
      </Tabs>

      {tab === 0 ? (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={openCreate}>
              New article
            </Button>
            <Button
              variant="outlined"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isLoading}
            >
              Seed from HelpCenter/articles
            </Button>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
          </Stack>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Topics</TableCell>
                  <TableCell align="right">Helpful</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(articlesQuery.data || []).map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{a.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={a.status} />
                    </TableCell>
                    <TableCell>
                      {(a.topics || []).slice(0, 3).map((t) => (
                        <Chip key={t} size="small" label={t} sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      {a.helpfulYes || 0}/{a.helpfulNo || 0}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEdit(a.id)}>
                        Edit
                      </Button>
                      {a.status !== 'published' ? (
                        <Button
                          size="small"
                          onClick={() => statusMutation.mutate({ id: a.id, status: 'published' })}
                        >
                          Publish
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => statusMutation.mutate({ id: a.id, status: 'archived' })}
                        >
                          Archive
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          if (window.confirm(`Delete ${a.id}?`)) deleteMutation.mutate(a.id);
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Alert severity="info">
            When users search with no results, mark an answer unhelpful, or suggest a question, items
            appear here. Resolve by creating/updating an article, then mark <b>doc_added</b>.
          </Alert>
          <TextField
            select
            size="small"
            label="Gap status"
            value={gapStatus}
            onChange={(e) => setGapStatus(e.target.value)}
            sx={{ maxWidth: 220 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="triaged">Triaged</MenuItem>
            <MenuItem value="doc_added">Doc added</MenuItem>
            <MenuItem value="wont_document">Won&apos;t document</MenuItem>
          </TextField>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Asks</TableCell>
                  <TableCell>Resolve</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(gapsQuery.data || []).map((g) => (
                  <TableRow key={g.id}>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2">{g.question}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {g.status}
                      </Typography>
                    </TableCell>
                    <TableCell>{g.source}</TableCell>
                    <TableCell>{g.role || '—'}</TableCell>
                    <TableCell align="right">{g.ask_count}</TableCell>
                    <TableCell>
                      <Stack spacing={1} sx={{ minWidth: 260 }}>
                        <TextField
                          size="small"
                          label="Notes"
                          value={gapNotes[g.id] || g.notes || ''}
                          onChange={(e) =>
                            setGapNotes((prev) => ({ ...prev, [g.id]: e.target.value }))
                          }
                        />
                        <TextField
                          select
                          size="small"
                          label="Link article"
                          value={g.article_id || ''}
                          onChange={(e) =>
                            gapMutation.mutate({
                              id: g.id,
                              articleId: e.target.value || null,
                              status: 'triaged',
                              notes: gapNotes[g.id] || g.notes || '',
                            })
                          }
                        >
                          <MenuItem value="">None</MenuItem>
                          {articleOptions.map((a) => (
                            <MenuItem key={a.id} value={a.id}>
                              {a.title}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              gapMutation.mutate({
                                id: g.id,
                                status: 'doc_added',
                                notes: gapNotes[g.id] || g.notes || '',
                                articleId: g.article_id || undefined,
                              })
                            }
                          >
                            Doc added
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              gapMutation.mutate({
                                id: g.id,
                                status: 'wont_document',
                                notes: gapNotes[g.id] || g.notes || '',
                              })
                            }
                          >
                            Won&apos;t document
                          </Button>
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit article' : 'New article'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="ID (slug)"
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              disabled={!!editingId}
              helperText="Stable id, e.g. zoom-substitute-vs-merge"
              fullWidth
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Short answer (required)"
              value={form.shortAnswer}
              onChange={(e) => setForm((f) => ({ ...f, shortAnswer: e.target.value }))}
              fullWidth
              required
              multiline
              minRows={3}
            />
            <TextField
              label="Detail (markdown)"
              value={form.bodyMarkdown}
              onChange={(e) => setForm((f) => ({ ...f, bodyMarkdown: e.target.value }))}
              fullWidth
              multiline
              minRows={6}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </TextField>
              <TextField
                label="Sort order"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                sx={{ width: 140 }}
              />
              <TextField
                label="Last verified"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.lastVerified}
                onChange={(e) => setForm((f) => ({ ...f, lastVerified: e.target.value }))}
              />
            </Stack>
            <TextField
              select
              SelectProps={{ multiple: true }}
              label="Roles"
              value={form.roles}
              onChange={(e) => setForm((f) => ({ ...f, roles: e.target.value }))}
              fullWidth
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              SelectProps={{ multiple: true }}
              label="Topics"
              value={form.topics}
              onChange={(e) => setForm((f) => ({ ...f, topics: e.target.value }))}
              fullWidth
            >
              {topicOptions.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Aliases (comma-separated)"
              value={form.aliases}
              onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Related article ids (comma-separated)"
              value={form.relatedIds}
              onChange={(e) => setForm((f) => ({ ...f, relatedIds: e.target.value }))}
              fullWidth
            />

            {editingId ? (
              <>
                <Divider />
                <Typography variant="subtitle2">Upload supporting document</Typography>
                <Button variant="outlined" component="label">
                  Choose file
                  <input
                    hidden
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        await helpAPI.adminUploadAttachment(uploadArticleId || editingId, fd);
                        toast.success('Uploaded');
                      } catch (err) {
                        toast.error(err.response?.data?.message || 'Upload failed');
                      }
                    }}
                  />
                </Button>
              </>
            ) : (
              <Alert severity="info">Save the article first, then upload attachments from Edit.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
